import { computed, DestroyRef, inject, Injectable, signal } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { CuiToastService } from '@innersource/common-ui-components';
import { firstValueFrom } from 'rxjs';
import { ChatAttachment, ChatSessionFile } from './chat.model';
import { ChatConversationService } from './chat-conversation.service';
import { ChatService } from './chat.service';

/** Manages files selected for the next message and downloads sent files. */
@Injectable()
export class ChatAttachmentsService {
  /** Services. */
  private readonly conversation = inject(ChatConversationService);
  private readonly chatService = inject(ChatService);
  private readonly toast = inject(CuiToastService);
  private readonly destroyRef = inject(DestroyRef);

  /** State. */
  private readonly sentIds = signal<Set<string>>(new Set());
  readonly pending = computed(() =>
    this.conversation
      .files()
      .filter(file => !file.messageIds?.length && !this.sentIds().has(file.id))
      .map(file => this.toAttachment(file))
  );
  readonly uploading = signal(false);
  private readonly previewUrls = signal<Map<string, string>>(new Map());
  private readonly loadingPreviews = new Set<string>();
  private uploadVersion = 0;

  /** Releases cached image URLs when the chat closes. */
  constructor() {
    this.destroyRef.onDestroy(() => this.clear());
  }

  /** Uploads selected files to the active conversation. */
  async addFiles(files: File[]): Promise<void> {
    if (
      !files.length ||
      this.uploading() ||
      this.conversation.loading() ||
      !this.conversation.selectedExpert()
    )
      return;
    const version = this.uploadVersion;
    this.uploading.set(true);
    try {
      const session = await this.conversation.ensureSession();
      for (const file of files) {
        if (version !== this.uploadVersion) return;
        const uploaded = await firstValueFrom(this.chatService.uploadSessionFile(session.id, file));
        if (version !== this.uploadVersion) return;
        if (uploaded.mimeType.startsWith('image/')) this.storePreview(uploaded.id, file);
        this.conversation.files.update(files => [...files, uploaded]);
      }
    } catch (error) {
      if (version !== this.uploadVersion) return;
      console.error('Could not upload chat file', error);
      const message = this.isUnsupportedFileType(error)
        ? 'This file type is not supported.'
        : 'Could not upload file. Check its type and size.';
      this.toast.show(message, { variant: 'error' });
    } finally {
      if (version === this.uploadVersion) this.uploading.set(false);
    }
  }

  /** Removes a draft file from the conversation. */
  async remove(file: ChatAttachment): Promise<void> {
    try {
      await firstValueFrom(this.chatService.deleteSessionFile(file.sessionId, file.id));
      this.conversation.files.update(files => files.filter(item => item.id !== file.id));
      this.releasePreview(file.id);
    } catch (error) {
      console.error('Could not delete chat file', error);
      this.toast.show('Could not remove file.', { variant: 'error' });
    }
  }

  /** Downloads a file attached to a sent message. */
  async download(file: ChatAttachment): Promise<void> {
    try {
      const blob = await firstValueFrom(
        this.chatService.downloadSessionFile(file.sessionId, file.id)
      );
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = file.name;
      document.body.append(link);
      link.click();
      link.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (error) {
      console.error('Could not download chat file', error);
      this.toast.show('Could not download file.', { variant: 'error' });
    }
  }

  /** Removes just-sent files from the composer while their IDs remain on the message. */
  markSent(files: ChatAttachment[]): void {
    this.sentIds.update(ids => new Set([...ids, ...files.map(file => file.id)]));
  }

  /** Returns a cached thumbnail URL for an image file. */
  previewUrl(file: ChatAttachment): string | undefined {
    return this.previewUrls().get(file.id);
  }

  /** Loads an authenticated image preview for a saved file. */
  async loadPreview(file: ChatAttachment): Promise<void> {
    if (
      !file.mimeType.startsWith('image/') ||
      this.previewUrl(file) ||
      this.loadingPreviews.has(file.id)
    )
      return;
    const version = this.uploadVersion;
    this.loadingPreviews.add(file.id);
    try {
      const blob = await firstValueFrom(
        this.chatService.downloadSessionFile(file.sessionId, file.id)
      );
      if (
        version !== this.uploadVersion ||
        !this.conversation.files().some(item => item.id === file.id)
      )
        return;
      this.storePreview(file.id, blob);
    } catch (error) {
      console.error('Could not load chat image preview', error);
    } finally {
      this.loadingPreviews.delete(file.id);
    }
  }

  /** Clears local composer state after changing conversations. */
  clear(): void {
    this.uploadVersion++;
    this.sentIds.set(new Set());
    this.uploading.set(false);
    for (const url of this.previewUrls().values()) URL.revokeObjectURL(url);
    this.previewUrls.set(new Map());
    this.loadingPreviews.clear();
  }

  /** Caches a browser URL for an uploaded or downloaded image. */
  private storePreview(fileId: string, blob: Blob): void {
    const url = URL.createObjectURL(blob);
    this.previewUrls.update(urls => new Map(urls).set(fileId, url));
  }

  /** Releases an image preview after removing its draft file. */
  private releasePreview(fileId: string): void {
    const url = this.previewUrls().get(fileId);
    if (url) URL.revokeObjectURL(url);
    this.previewUrls.update(urls => {
      const remaining = new Map(urls);
      remaining.delete(fileId);
      return remaining;
    });
  }

  /** Recognizes an unsupported media type response from the upload API. */
  private isUnsupportedFileType(error: unknown): boolean {
    if (!(error instanceof HttpErrorResponse)) return false;
    if (error.status === 415) return true;
    const payload: unknown = error.error;
    if (!payload || typeof payload !== 'object' || !('detail' in payload)) return false;
    const detail = payload.detail;
    return typeof detail === 'string' && /unsupported|media type|file type/i.test(detail);
  }

  /** Converts API file metadata to display data. */
  private toAttachment(file: ChatSessionFile): ChatAttachment {
    return {
      id: file.id,
      sessionId: file.sessionId,
      name: file.originalName,
      mimeType: file.mimeType,
    };
  }
}
